"use client";

import {useEffect, useState} from "react";
import {VoiceOption} from "@/types/agencies";
import {verifyScriptVoice} from "@/utils/scripts/verifyScriptVoice";

export type VoiceVerifyStatus = "unknown" | "allowed" | "missing";

/**
 * Point-lookup whether the script's stored voice is still allowed.
 * Independent of which editor tab is visible — save/Next must not wait on Agent page mount.
 * Fail-open on any error (including session expiry); only a confirmed 404 is `missing`.
 */
export function useVerifyScriptVoice(voiceId: string | undefined): {
   status: VoiceVerifyStatus;
   voice: VoiceOption | null;
   isVoiceAllowed: boolean | null;
} {
   const [status, setStatus] = useState<VoiceVerifyStatus>("unknown");
   const [voice, setVoice] = useState<VoiceOption | null>(null);

   useEffect(() => {
      if (!voiceId?.trim()) {
         setStatus("unknown");
         setVoice(null);
         return;
      }

      setStatus("unknown");
      setVoice(null);
      let cancelled = false;

      (async () => {
         try {
            const result = await verifyScriptVoice(voiceId);
            if (cancelled) {
               return;
            }
            setStatus(result.status);
            setVoice(result.status === "allowed" ? result.voice : null);
         } catch {
            if (!cancelled) {
               setStatus("unknown");
               setVoice(null);
            }
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [voiceId]);

   let isVoiceAllowed: boolean | null = null;
   if (status === "missing") {
      isVoiceAllowed = false;
   } else if (status === "allowed") {
      isVoiceAllowed = true;
   }

   return {status, voice, isVoiceAllowed};
}
